import {
  Badge,
  Icons,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@commenda/kakashi';
import { cn } from '@commenda/utils';
import { useState } from 'react';

import { DeleteConfirmationModal } from '../../molecules/modals/ConfirmationModal';
import { useReport } from '../context/ReportContext';
import type { IReportSavedView } from '../types/report';
import { SaveViewDialog } from './SaveViewDialog';

const ROW_CLASS =
  'flex items-center gap-sm px-lg py-md hover:bg-bg-primary-hover transition-colors';
const ACTION_CLASS = cn(ROW_CLASS, 'w-full text-left text-text-primary');

/** Saved-view switcher in the same shape as the table `TableViewSelector`. */
export const SavedViewsMenu = () => {
  const {
    views,
    activeViewId,
    isDirty,
    openView,
    saveView,
    saveViewAs,
    renameView,
    deleteView,
  } = useReport();
  const [isOpen, setIsOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [editingView, setEditingView] = useState<IReportSavedView | null>(null);
  const [viewToDelete, setViewToDelete] = useState<IReportSavedView | null>(
    null
  );

  const builtInViews = views.filter((v) => v.isDefault);
  const customViews = views.filter((v) => !v.isDefault);
  const selectedView = customViews.find((v) => v.id === activeViewId) ?? null;
  const activeBuiltIn =
    builtInViews.find((v) => v.id === activeViewId) ?? builtInViews[0];
  const canSave = isDirty;

  const select = (id: string) => {
    openView(id);
    setIsOpen(false);
  };

  const openSaveDialog = (view: IReportSavedView | null) => {
    setEditingView(view);
    setSaveOpen(true);
    setIsOpen(false);
  };

  const handleSave = (name: string) => {
    if (!editingView) {
      saveViewAs(name);
      return;
    }
    renameView(editingView.id, name);
    if (editingView.id === activeViewId) saveView();
  };

  return (
    <>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              'flex items-center gap-sm rounded-md border border-border-primary bg-bg-primary px-lg py-sm',
              'cursor-pointer transition-colors hover:bg-bg-primary-hover',
              'txt-small-medium text-text-primary'
            )}
          >
            <span className="max-w-[160px] truncate">
              {selectedView?.name ?? activeBuiltIn?.name ?? 'Default view'}
            </span>
            {isDirty && (
              <Badge variant="gray" size="sm">
                Modified
              </Badge>
            )}
            <Icons.ChevronDown className="size-4 text-fg-quaternary" />
          </button>
        </PopoverTrigger>

        <PopoverContent align="start" sideOffset={4} className="w-[280px] p-0">
          <div className="flex flex-col">
            {builtInViews.map((view) => {
              const isSelected = !selectedView && view.id === activeBuiltIn?.id;
              return (
                <button
                  key={view.id}
                  type="button"
                  onClick={() => select(view.id)}
                  className={cn(
                    ACTION_CLASS,
                    'justify-between',
                    isSelected && 'bg-bg-secondary'
                  )}
                >
                  <span
                    className={cn(
                      'txt-small-medium',
                      isSelected && 'text-fg-brand-primary'
                    )}
                  >
                    {view.name}
                  </span>
                  {isSelected && (
                    <Icons.Check className="size-4 text-fg-brand-primary" />
                  )}
                </button>
              );
            })}

            {customViews.length > 0 && (
              <div className="border-t border-border-primary py-sm">
                {customViews.map((view) => {
                  const isSelected = view.id === activeViewId;
                  return (
                    <div
                      key={view.id}
                      className={cn(
                        ROW_CLASS,
                        'group',
                        isSelected && 'bg-bg-secondary'
                      )}
                    >
                      <button
                        type="button"
                        onClick={() => select(view.id)}
                        className="flex min-w-0 flex-1 items-center justify-between text-left"
                      >
                        <span
                          className={cn(
                            'truncate txt-small-medium',
                            isSelected
                              ? 'text-fg-brand-primary'
                              : 'text-text-primary'
                          )}
                        >
                          {view.name}
                        </span>
                        {isSelected && (
                          <Icons.Check className="ml-sm size-4 shrink-0 text-fg-brand-primary" />
                        )}
                      </button>
                      <div className="flex shrink-0 items-center gap-xs opacity-0 transition-opacity group-hover:opacity-100">
                        <button
                          type="button"
                          onClick={() => openSaveDialog(view)}
                          className="rounded p-xs transition-colors hover:bg-bg-secondary"
                          title="Edit view"
                        >
                          <Icons.Edit02 className="size-3.5 text-fg-quaternary hover:text-fg-secondary" />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setViewToDelete(view);
                            setIsOpen(false);
                          }}
                          className="rounded p-xs transition-colors hover:bg-utility-error-50"
                          title="Delete view"
                        >
                          <Icons.Trash01 className="size-3.5 text-fg-quaternary hover:text-utility-error-600" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="border-t border-border-primary" />

            {selectedView && isDirty ? (
              <>
                <button
                  type="button"
                  onClick={() => {
                    saveView();
                    setIsOpen(false);
                  }}
                  className={ACTION_CLASS}
                >
                  <Icons.Check className="size-4" />
                  <span className="txt-small-medium">Update view</span>
                </button>
                <button
                  type="button"
                  onClick={() => openSaveDialog(null)}
                  className={ACTION_CLASS}
                >
                  <Icons.Plus className="size-4" />
                  <span className="txt-small-medium">Create new</span>
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => openSaveDialog(null)}
                disabled={!canSave}
                className={cn(
                  'flex w-full items-center gap-sm px-lg py-md text-left transition-colors',
                  canSave
                    ? 'text-text-primary hover:bg-bg-primary-hover'
                    : 'cursor-not-allowed text-text-disabled'
                )}
              >
                <Icons.Plus className="size-4" />
                <span className="txt-small-medium">Save current view</span>
              </button>
            )}
          </div>
        </PopoverContent>
      </Popover>

      <SaveViewDialog
        open={saveOpen}
        onClose={() => {
          setSaveOpen(false);
          setEditingView(null);
        }}
        onSave={handleSave}
        existingName={editingView?.name}
      />

      <DeleteConfirmationModal
        isOpen={viewToDelete !== null}
        onClose={() => setViewToDelete(null)}
        onConfirm={() => {
          if (viewToDelete) deleteView(viewToDelete.id);
        }}
        title="Delete view?"
        message={`Are you sure you want to delete "${viewToDelete?.name ?? ''}"? This action cannot be undone.`}
      />
    </>
  );
};
